import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Lightbulb, Fan, Snowflake, ShieldCheck, ArrowUpRight, Sparkles } from "lucide-react";
import SectionHeading from "./SectionHeading";

const SCRIPT = [
  { role: "user", text: "I'm going to sleep." },
  { role: "ai", text: "Good night. I've turned off the lights, set the fan to level 2, adjusted the AC, and enabled night security." },
];

const DEVICES = [
  { icon: Lightbulb, label: "Lights", value: "Off" },
  { icon: Fan, label: "Ceiling Fan", value: "Level 2" },
  { icon: Snowflake, label: "AC", value: "24°C" },
  { icon: ShieldCheck, label: "Night Security", value: "Enabled" },
];

export default function SefKamHomeSection({ compact = false }) {
  const [step, setStep] = useState(0); // 0 none, 1 user, 2 ai + devices
  const ref = useRef(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStarted(true); }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    const run = () => {
      setStep(0);
      const t1 = setTimeout(() => setStep(1), 900);
      const t2 = setTimeout(() => setStep(2), 2400);
      return [t1, t2];
    };
    let timers = run();
    const loop = setInterval(() => { timers.forEach(clearTimeout); timers = run(); }, 7000);
    return () => { timers.forEach(clearTimeout); clearInterval(loop); };
  }, [started]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-[#0B1220] py-24 md:py-32 grain" data-testid="sefkam-home-section">
      <div className="pointer-events-none absolute right-0 top-1/3 h-[34rem] w-[34rem] rounded-full opacity-20 blur-3xl accent-bg" />
      <div className="container-x relative grid items-center gap-16 lg:grid-cols-2">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-cyan-300" data-testid="coming-soon-badge">
            <Sparkles size={13} /> Coming Soon
          </span>
          <SectionHeading
            eyebrow="Meet SefKam Home"
            title="Your home. Connected. Automated. Intelligent."
            dark
            max="max-w-xl"
          />
          <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-400">
            SefKam Home is our vision for an AI-powered smart-home platform that lets people
            control and automate everyday devices through one intelligent experience.
          </p>
          {!compact && (
            <Link to="/sefkam-home" className="btn-primary mt-8" data-testid="discover-sefkam-home">
              Discover SefKam Home <ArrowUpRight size={16} />
            </Link>
          )}
        </div>

        {/* Chat + device mockup */}
        <div className="relative">
          <div className="rounded-[2rem] border border-white/10 bg-[#07111F]/80 p-5 backdrop-blur-xl shadow-2xl">
            <div className="flex items-center justify-between border-b border-white/5 pb-4">
              <div className="flex items-center gap-2.5">
                <span className="grid h-9 w-9 place-items-center rounded-xl accent-bg text-[#07111F]"><Sparkles size={16} /></span>
                <div>
                  <p className="text-sm font-semibold text-white">SefKam Assistant</p>
                  <p className="text-[10px] text-emerald-300">online</p>
                </div>
              </div>
              <span className="font-mono text-[10px] text-slate-500">home.ai</span>
            </div>

            <div className="min-h-[168px] space-y-3 py-5">
              <AnimatePresence>
                {step >= 1 && (
                  <motion.div key="u" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex justify-end">
                    <p className="max-w-[80%] rounded-2xl rounded-br-md accent-bg px-4 py-2.5 text-sm font-medium text-[#07111F]" data-testid="chat-user">
                      {SCRIPT[0].text}
                    </p>
                  </motion.div>
                )}
                {step === 1 && (
                  <motion.div key="typing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex gap-1.5 pl-1">
                    {[0, 1, 2].map((d) => (
                      <motion.span key={d} className="h-2 w-2 rounded-full bg-slate-500"
                        animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: d * 0.2 }} />
                    ))}
                  </motion.div>
                )}
                {step >= 2 && (
                  <motion.div key="ai" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex justify-start">
                    <p className="max-w-[85%] rounded-2xl rounded-bl-md border border-white/10 bg-white/5 px-4 py-2.5 text-sm leading-relaxed text-slate-200" data-testid="chat-ai">
                      {SCRIPT[1].text}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              {DEVICES.map((d, i) => {
                const Icon = d.icon;
                const active = step >= 2;
                return (
                  <motion.div
                    key={d.label}
                    animate={active ? { opacity: 1, scale: 1 } : { opacity: 0.5, scale: 0.98 }}
                    transition={{ delay: active ? 0.15 * i : 0, duration: 0.4 }}
                    className={`flex items-center gap-2.5 rounded-2xl border p-3 transition-colors duration-500 ${active ? "border-cyan-400/30 bg-cyan-400/5" : "border-white/10 bg-white/5"}`}
                    data-testid={`device-${i}`}
                  >
                    <span className={`grid h-8 w-8 place-items-center rounded-lg ${active ? "accent-bg text-[#07111F]" : "bg-white/10 text-slate-400"}`}>
                      <Icon size={15} />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-[11px] text-slate-400">{d.label}</p>
                      <p className="truncate text-xs font-semibold text-white">{active ? d.value : "—"}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
